import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Box, Button, Heading, Text } from '@chakra-ui/react';
import { motion } from 'framer-motion';
import LandingHeader from '../../components/LandingHeader/LandingHeader';
import LandingFooter from '../../components/LandingFooter/LandingFooter';
import SubPageHero from '../../components/SubPageHero/SubPageHero';
import Seo from '../../components/Seo/Seo';
import './Landing.css';
import './Team.css';
import Icon from '../../components/ui/Icon';
import { TEAM_MEMBERS } from './teamMembers';

const MotionBox = motion(Box);
const publicUrl = (import.meta.env.BASE_URL || '').replace(/\/$/, '');
const heroImageUrl = `${publicUrl}/images/team-hero.jpg`;

// Leadership data now lives in ./teamMembers.js (shared with the profile pages).

// Key milestones — moved here from the Products & Pipeline page, rendered as a
// horizontal timeline.
const MILESTONES = [
  {
    year: '2022',
    color: '#6E1B28',
    items: ['Company founded'],
  },
  {
    year: '2023',
    color: '#8A2233',
    items: ['INAD @ FDA-CVM open', 'Donor herd established', 'Clinical trials started'],
  },
  {
    year: '2024',
    color: '#A83B4C',
    items: ['Clinical trials continue', 'New pipeline product exploration', 'Manufacturing facility upfit'],
  },
  {
    year: '2025',
    color: '#C55E63',
    items: ['Clinical trial conclusion', 'NAP-X preclinical development started'],
  },
  {
    year: '2026',
    color: '#E79A8C',
    items: ['MUMS designation received', 'BIO-PLY™ manufacturing scaleup'],
  },
];

const VALUES = [
  {
    icon: 'science',
    title: 'Lab to clinic',
    body:
      'BIO-PLY\u2122 began in a leading equine research lab and was spun out with its inventors at the helm, building therapeutics on a foundation of peer-reviewed science.',
    proof: 'Tested in vitro \u00b7 Active in vivo',
  },
  {
    icon: 'pets',
    title: 'Outcomes that matter',
    body:
      'We measure success by horses returning to work. In our pilot trial, treated horses returned to athletic performance at far higher rates than historical norms.',
    proof: '85% return to athletic function',
  },
  {
    icon: 'verified',
    title: 'Pharmaceutical-grade rigor',
    body:
      'BIO-PLY\u2122 is manufactured under cGMP from a closed, disease-screened herd, with strict release criteria and per-lot activity testing before any product ships.',
    proof: 'cGMP \u00b7 Closed donor herd',
  },
];

export default function Team() {
  const navigate = useNavigate();

  return (
    <div className="landing-page">
      <Seo
        title="About Qentoros | Our Values & Team"
        description="Meet the veterinary scientists and clinicians behind Qentoros and BIO-PLY™ — and the values guiding our science, from lab to clinic."
        path="/about/team"
      />
      <LandingHeader />

      <main id="main" tabIndex={-1}>
        <SubPageHero
          eyebrow="About"
          title="About Qentoros"
          subtitle="The values that guide our science — and the veterinary scientists and clinicians building the next generation of equine therapeutics."
          backgroundImage={heroImageUrl}
        />

        {/* Values */}
        <section
          id="values"
          className="landing-section team-values-section"
          aria-labelledby="values-heading"
        >
          <header className="team-section-header">
            <p className="team-eyebrow">Our values</p>
            <div className="team-accent" aria-hidden="true" />
            <Heading
              as="h2"
              id="values-heading"
              size="xl"
              textAlign="center"
              color="gray.900"
              mb={2}
            >
              How we work
            </Heading>
          </header>

          <div className="team-values">
            {VALUES.map((value, index) => (
              <MotionBox
                key={value.title}
                className="team-value-card"
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-24px' }}
                transition={{ duration: 0.4, delay: index * 0.06 }}
              >
                <div className="team-value-card-top">
                  <span className="team-value-number" aria-hidden="true">
                    {String(index + 1).padStart(2, '0')}
                  </span>
                  <div className="team-value-icon" aria-hidden="true">
                    <Icon name={value.icon} />
                  </div>
                </div>
                <h3 className="team-value-title">{value.title}</h3>
                <p className="team-value-body">{value.body}</p>
                {value.proof && (
                  <div className="team-value-proof" aria-hidden="true">
                    <Icon name="check_circle" className="team-value-proof-icon" />
                    <span className="team-value-proof-text">{value.proof}</span>
                  </div>
                )}
              </MotionBox>
            ))}
          </div>
        </section>

        {/* Key milestones — horizontal timeline */}
        <section
          id="milestones"
          className="landing-section team-milestones"
          aria-labelledby="milestones-heading"
        >
          <header className="team-section-header">
            <p className="team-eyebrow">Our journey</p>
            <div className="team-accent" aria-hidden="true" />
            <Heading
              as="h2"
              id="milestones-heading"
              size="xl"
              textAlign="center"
              color="gray.900"
              mb={2}
            >
              Key milestones, 2022–2026
            </Heading>
            <Text
              textAlign="center"
              color="gray.600"
              fontSize="lg"
              maxW="56ch"
              mx="auto"
            >
              From founding to first product — the milestones that brought
              BIO-PLY™ to the clinic and built the pipeline behind it.
            </Text>
          </header>

          <ol className="team-timeline">
            {MILESTONES.map((milestone, index) => (
              <MotionBox
                as="li"
                key={milestone.year}
                className="team-timeline-step"
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-24px' }}
                transition={{ duration: 0.4, delay: index * 0.06 }}
              >
                <div className="team-timeline-rail" aria-hidden="true">
                  <span
                    className="team-timeline-node"
                    style={{ borderColor: milestone.color }}
                  />
                </div>
                <div className="team-timeline-year">{milestone.year}</div>
                <ul className="team-timeline-items">
                  {milestone.items.map((item) => (
                    <li key={item}>
                      <span
                        className="team-timeline-bullet"
                        style={{ background: milestone.color }}
                        aria-hidden="true"
                      />
                      {item}
                    </li>
                  ))}
                </ul>
              </MotionBox>
            ))}
          </ol>
        </section>

        {/* Leadership */}
        <section
          id="leadership"
          className="landing-section team-leadership"
          aria-labelledby="leadership-heading"
        >
          <header className="team-section-header">
            <p className="team-eyebrow">Leadership</p>
            <div className="team-accent" aria-hidden="true" />
            <Heading
              as="h2"
              id="leadership-heading"
              size="2xl"
              textAlign="center"
              color="gray.900"
              mb={2}
            >
              The people leading Qentoros
            </Heading>
            <Text
              textAlign="center"
              color="gray.600"
              fontSize="lg"
              maxW="56ch"
              mx="auto"
            >
              A founding team that spans bench science, equine surgery, and
              regulatory rigor.
            </Text>
          </header>

          <div className="team-leaders">
            {TEAM_MEMBERS.map((member, index) => (
              <MotionBox
                as={Link}
                to={`/about/team/${member.slug}`}
                key={member.name}
                className={`team-leader-card team-leader-card--link team-leader-card--${member.accent}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-40px' }}
                transition={{ duration: 0.45, delay: index * 0.08 }}
              >
                <div
                  className={`team-leader-portrait${
                    member.photo ? ' team-leader-portrait--photo' : ''
                  }`}
                  aria-hidden="true"
                >
                  {member.photo ? (
                    <img
                      src={member.photo}
                      alt=""
                      className="team-leader-photo"
                      loading="lazy"
                    />
                  ) : (
                    <span className="team-leader-initials">{member.initials}</span>
                  )}
                </div>
                <div className="team-leader-body">
                  <h3 className="team-leader-name">{member.name}</h3>
                  <p className="team-leader-role">{member.role}</p>
                  <ul
                    className="team-credential-list"
                    aria-label={`${member.name} credentials`}
                  >
                    {member.credentials.map((c) => (
                      <li key={c} className="team-credential-pill">
                        {c}
                      </li>
                    ))}
                  </ul>
                  <p className="team-leader-bio">{member.bio}</p>
                  <span className="team-leader-profile-link">
                    View profile
                    <Icon name="arrow_forward" aria-hidden="true" />
                  </span>
                </div>
              </MotionBox>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="team-cta" aria-labelledby="team-cta-heading">
          <div className="team-cta-inner">
            <Heading
              as="h2"
              id="team-cta-heading"
              size="xl"
              color="white"
              mb={3}
            >
              Partner with us
            </Heading>
            <Text color="whiteAlpha.900" maxW="52ch" mx="auto" mb={6}>
              {'Whether you\u2019re a veterinarian, researcher, or investor, we would love to hear from you.'}
            </Text>
            <div className="team-cta-actions">
              <Button
                onClick={() => navigate('/contact')}
                size="lg"
                bg="white"
                color="maroon.800"
                _hover={{ bg: 'whiteAlpha.900' }}
                borderRadius="var(--radius-md)"
                fontWeight={600}
              >
                Contact us
              </Button>
            </div>
          </div>
        </section>

        {/* Footer */}
      </main>

      <LandingFooter />
    </div>
  );
}
